// Migrated 1:1 from @nexato/nx-core-module (#751). The control was declared but never
// exported by the library module, so it was missed when the repos were merged — its
// three consumers (tenant-, company- and billing-invoice-receiver-address-dialog)
// failed at runtime with NG01203 (no value accessor for 'country').
import { AfterViewInit, ChangeDetectorRef, Component, forwardRef, Injector, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, NgControl, Validators } from '@angular/forms';
import { Dropdown, DropdownChangeEvent } from 'primeng/dropdown';

interface Country {
    name: string;
    id: string;
}

@Component({
    selector: 'nx-edit-selectCountry-input',
    template: `
    <div class="nx-edit-selectCountry">
        <label for="multiSelect">{{label}}<ng-container *ngIf=isRequired()>*</ng-container></label>
        <p-dropdown
            #dropdown
            [options]="options"
            [(ngModel)]="selectedCountry"
            (onChange)="onInputChange($event)" 
            optionLabel="name"
            [appendTo]="'body'"
            [virtualScroll]="true"
            [filter]="true"
            [disabled]="isDisabled"
            [panelStyleClass]="'nx-edit-selectCountry'"
            filterPlaceholder="Suchen..."
            filterBy="name"
            [resetFilterOnHide]="true"
            [virtualScrollItemSize]="20"
            [autoDisplayFirst]=false
            [placeholder]="placeholder" />
        <small *ngIf="helpText">{{helpText}}</small>
    </div>
  `,
    styles: [`
  `],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => EditSelectCountryInputComponent),
            multi: true
        }
    ]
})
export class EditSelectCountryInputComponent implements ControlValueAccessor, OnInit, AfterViewInit, OnDestroy {

    @ViewChild('dropdown') pDropdown: Dropdown;
    private mutationObserver: MutationObserver;

    ngControl: NgControl;
    isDisabled: boolean;
    @Input() label = 'Land';
    @Input() placeholder: string = 'Land auswählen';
    @Input() helpText: string;

    selectedCountry: Country;
    options: Country[];

    private onChange: (value: any) => void;
    private onTouched: () => void;

    constructor(
        private _injector: Injector,
        private cdr: ChangeDetectorRef
    ) {
        this.options = this.getCountries();
    }
    ngAfterViewInit(): void {
        //this.multiSelect.updateModel(this.selected);
        this.cdr.detectChanges();
        this.observeClassChanges();
    }

    ngOnInit() {
        this.ngControl = this._injector.get(NgControl);
        this.ngControl.valueAccessor = this;
    }

    ngOnDestroy() {
        this.mutationObserver?.disconnect();
      }

    writeValue(id: string): void {
        // convert the array of ids to an array of objects by using
        // the available options to feed this into the multiselect
        this.selectedCountry = this.options.find(option => option.id === id);
    }

    registerOnChange(fn: (selected: any[]) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    setDisabledState?(isDisabled: boolean): void {
        this.isDisabled = isDisabled;
    }

    onInputChange(event: DropdownChangeEvent): void {
        this.selectedCountry = event.value;
        if (this.onChange) {
            // reduce the selected value to its id
            this.onChange(this.selectedCountry ? this.selectedCountry?.id : undefined);
        }
    }

    onBlur(): void {
        if (this.onTouched) {
            this.onTouched();
        }
    }

    isRequired(): boolean {
        return this.ngControl?.control?.hasValidator(Validators.required);
    }

    getCountries(): Country[] {
        let arrayOfCountries = Object.keys(this.countries).map(key => ({ id: key, name: this.countries[key] }));
        // sort the array by name
        arrayOfCountries.sort((a, b) => a.name.localeCompare(b.name));
        return arrayOfCountries;
    }

    /**
     * Workaround: for whatever reason the dropdown element does not fire the blur event. We work
     * around this by observing the class changes: when the class p-inputwrapper-focus gets removed,
     * we fire the event and inform the form control that the input has been touched.
     */
    observeClassChanges() {
        const targetNode = this.pDropdown?.el?.nativeElement;
        if (!targetNode) {
          // target not found
          return;
        }
        this.mutationObserver = new MutationObserver((mutationsList) => {
          for (const mutation of mutationsList) {
            if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
              const classList = (mutation.target as HTMLElement).classList;
              if (!classList.contains('p-inputwrapper-focus')) {
                this.onBlur();
              }
            }
          }
        });
        this.mutationObserver.observe(targetNode, {
          attributes: true,
          attributeFilter: ['class']
        });
      }


    // taken from here: https://github.com/michaelwittig/node-i18n-iso-countries/blob/master/langs/de.json
    // reduced all entries with multiple names for one value
    private countries = {
        "AF": "Afghanistan",
        "EG": "Ägypten",
        "AX": "Åland",
        "AL": "Albanien",
        "DZ": "Algerien",
        "AS": "Amerikanisch-Samoa",
        "VI": "Amerikanische Jungferninseln",
        "AD": "Andorra",
        "AO": "Angola",
        "AI": "Anguilla",
        "AQ": "Antarktis",
        "AG": "Antigua und Barbuda",
        "GQ": "Äquatorialguinea",
        "AR": "Argentinien",
        "AM": "Armenien",
        "AW": "Aruba",
        "AZ": "Aserbaidschan",
        "ET": "Äthiopien",
        "AU": "Australien",
        "BS": "Bahamas",
        "BH": "Bahrain",
        "BD": "Bangladesch",
        "BB": "Barbados",
        "BY": "Belarus",
        "BE": "Belgien",
        "BZ": "Belize",
        "BJ": "Benin",
        "BM": "Bermuda",
        "BT": "Bhutan",
        "BO": "Bolivien",
        "BQ": "Bonaire",
        "BA": "Bosnien und Herzegowina",
        "BW": "Botswana",
        "BV": "Bouvetinsel",
        "BR": "Brasilien",
        "VG": "Britische Jungferninseln",
        "IO": "Britisches Territorium im Indischen Ozean",
        "BN": "Brunei Darussalam",
        "BG": "Bulgarien",
        "BF": "Burkina Faso",
        "BI": "Burundi",
        "CL": "Chile",
        "CN": "China",
        "CK": "Cookinseln",
        "CR": "Costa Rica",
        "CI": "Elfenbeinküste",
        "CW": "Curaçao",
        "DK": "Dänemark",
        "DE": "Deutschland",
        "DM": "Dominica",
        "DO": "Dominikanische Republik",
        "DJ": "Dschibuti",
        "EC": "Ecuador",
        "SV": "El Salvador",
        "ER": "Eritrea",
        "EE": "Estland",
        "FK": "Falklandinseln",
        "FO": "Färöer",
        "FJ": "Fidschi",
        "FI": "Finnland",
        "FR": "Frankreich",
        "GF": "Französisch-Guayana",
        "PF": "Französisch-Polynesien",
        "TF": "Französische Süd- und Antarktisgebiete",
        "GA": "Gabun",
        "GM": "Gambia",
        "GE": "Georgien",
        "GH": "Ghana",
        "GI": "Gibraltar",
        "GD": "Grenada",
        "GR": "Griechenland",
        "GL": "Grönland",
        "GP": "Guadeloupe",
        "GU": "Guam",
        "GT": "Guatemala",
        "GG": "Guernsey",
        "GN": "Guinea",
        "GW": "Guinea-Bissau",
        "GY": "Guyana",
        "HT": "Haiti",
        "HM": "Heard und McDonaldinseln",
        "HN": "Honduras",
        "HK": "Hong Kong",
        "IN": "Indien",
        "ID": "Indonesien",
        "IM": "Insel Man",
        "IQ": "Irak",
        "IR": "Iran",
        "IE": "Irland",
        "IS": "Island",
        "IL": "Israel",
        "IT": "Italien",
        "JM": "Jamaika",
        "JP": "Japan",
        "YE": "Jemen",
        "JE": "Jersey",
        "JO": "Jordanien",
        "KY": "Kaimaninseln",
        "KH": "Kambodscha",
        "CM": "Kamerun",
        "CA": "Kanada",
        "CV": "Kap Verde",
        "KZ": "Kasachstan",
        "QA": "Katar",
        "KE": "Kenia",
        "KG": "Kirgisistan",
        "KI": "Kiribati",
        "CC": "Kokosinseln",
        "CO": "Kolumbien",
        "KM": "Komoren",
        "CD": "Kongo",
        "KP": "Nordkorea",
        "KR": "Südkorea",
        "HR": "Kroatien",
        "CU": "Kuba",
        "KW": "Kuwait",
        "LA": "Laos",
        "LS": "Lesotho",
        "LV": "Lettland",
        "LB": "Libanon",
        "LR": "Liberia",
        "LY": "Libyen",
        "LI": "Liechtenstein",
        "LT": "Litauen",
        "LU": "Luxemburg",
        "MO": "Macao",
        "MG": "Madagaskar",
        "MW": "Malawi",
        "MY": "Malaysia",
        "MV": "Malediven",
        "ML": "Mali",
        "MT": "Malta",
        "MA": "Marokko",
        "MH": "Marshallinseln",
        "MQ": "Martinique",
        "MR": "Mauretanien",
        "MU": "Mauritius",
        "YT": "Mayotte",
        "MX": "Mexiko",
        "FM": "Mikronesien",
        "MD": "Moldawien",
        "MC": "Monaco",
        "MN": "Mongolei",
        "ME": "Montenegro",
        "MS": "Montserrat",
        "MZ": "Mosambik",
        "MM": "Myanmar",
        "NA": "Namibia",
        "NR": "Nauru",
        "NP": "Nepal",
        "NC": "Neukaledonien",
        "NZ": "Neuseeland",
        "NI": "Nicaragua",
        "NL": "Niederlande",
        "NE": "Niger",
        "NG": "Nigeria",
        "NU": "Niue",
        "MK": "Nordmazedonien",
        "MP": "Nördliche Marianen",
        "NF": "Norfolkinsel",
        "NO": "Norwegen",
        "OM": "Oman",
        "AT": "Österreich",
        "TL": "Osttimor",
        "PK": "Pakistan",
        "PS": "Staat Palästina",
        "PW": "Palau",
        "PA": "Panama",
        "PG": "Papua-Neuguinea",
        "PY": "Paraguay",
        "PE": "Peru",
        "PH": "Philippinen",
        "PN": "Pitcairninseln",
        "PL": "Polen",
        "PT": "Portugal",
        "PR": "Puerto Rico",
        "TW": "Taiwan",
        "CG": "Republik Kongo",
        "RE": "Réunion",
        "RW": "Ruanda",
        "RO": "Rumänien",
        "RU": "Russland",
        "BL": "Saint-Barthélemy",
        "MF": "Saint-Martin",
        "SB": "Salomonen",
        "ZM": "Sambia",
        "WS": "Samoa",
        "SM": "San Marino",
        "ST": "São Tomé und Príncipe",
        "SA": "Saudi-Arabien",
        "SE": "Schweden",
        "CH": "Schweiz",
        "SN": "Senegal",
        "RS": "Serbien",
        "SC": "Seychellen",
        "SL": "Sierra Leone",
        "ZW": "Simbabwe",
        "SG": "Singapur",
        "SX": "Sint Maarten",
        "SK": "Slowakei",
        "SI": "Slowenien",
        "SO": "Somalia",
        "ES": "Spanien",
        "LK": "Sri Lanka",
        "SH": "St. Helena",
        "KN": "St. Kitts und Nevis",
        "LC": "St. Lucia",
        "PM": "Saint-Pierre und Miquelon",
        "VC": "St. Vincent und die Grenadinen",
        "ZA": "Südafrika",
        "SD": "Sudan",
        "GS": "Südgeorgien und die Südlichen Sandwichinseln",
        "SS": "Südsudan",
        "SR": "Suriname",
        "SJ": "Svalbard und Jan Mayen",
        "SZ": "Eswatini",
        "SY": "Syrien, Arabische Republik",
        "TJ": "Tadschikistan",
        "TZ": "Tansania",
        "TH": "Thailand",
        "TG": "Togo",
        "TK": "Tokelau",
        "TO": "Tonga",
        "TT": "Trinidad und Tobago",
        "TD": "Tschad",
        "CZ": "Tschechien",
        "TN": "Tunesien",
        "TR": "Türkei",
        "TM": "Turkmenistan",
        "TC": "Turks- und Caicosinseln",
        "TV": "Tuvalu",
        "UG": "Uganda",
        "UA": "Ukraine",
        "HU": "Ungarn",
        "UM": "United States Minor Outlying Islands",
        "UY": "Uruguay",
        "UZ": "Usbekistan",
        "VU": "Vanuatu",
        "VA": "Vatikanstadt",
        "VE": "Venezuela",
        "AE": "Vereinigte Arabische Emirate",
        "US": "Vereinigte Staaten von Amerika",
        "GB": "Vereinigtes Königreich",
        "VN": "Vietnam",
        "WF": "Wallis und Futuna",
        "CX": "Weihnachtsinsel",
        "EH": "Westsahara",
        "CF": "Zentralafrikanische Republik",
        "CY": "Zypern",
        "XK": "Kosovo"
    };

}